import { useCallback, useEffect, useRef, useState } from "react";
import {
  authDetail,
  authLogin,
  AuthLoginPubsResponse,
  authPubOwnerToken,
  deleteNaverToken,
  LoginRouterState,
  sellerLogin,
} from "../../../api/auth";
import {
  LOCAL_STORAGE_PUB_ID,
  LOCAL_STORAGE_TOKEN_TYPE,
  LOCAL_STORAGE_ACCESS_KEY,
  LOCAL_STORAGE_USER_ID,
  LOCAL_STORAGE_SUPER_ADMIN_KEY,
  clearCredential,
} from "../../../utils/network";
import { useLocation, useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { UserState } from "../../../recoil/user.atom";
import useSnsLogin from "./use-sns-login";
import { readSnsCallbackParams } from "../../../utils/sns-oauth";

const useLogin = () => {
  const [textId, setTextId] = useState("");
  const [password, setPassword] = useState("");
  const [pubOwners, setPubOwners] = useState<AuthLoginPubsResponse[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const setUser = useSetRecoilState(UserState);
  const snsCallbackHandled = useRef(false);
  const snsStateRef = useRef({
    setIsNaver: (_: boolean) => {},
    setNaverEmail: (_: string) => {},
    setNaverPopupType: (_: string) => {},
  });

  // 유저 정보 조회 및 슈퍼관리자 로그인 처리
  const handleAuthDetail = useCallback(
    async (token?: string, pubOwners: AuthLoginPubsResponse[] = []) => {
      const res = await authDetail(token);
      if (res?.role === "ROLE_SUPER_ADMIN") {
        // 유저 권한이 슈퍼 관리자인 경우
        localStorage.setItem(
          LOCAL_STORAGE_SUPER_ADMIN_KEY,
          `Bearer ${res.accessToken}`,
        );
        localStorage.removeItem(LOCAL_STORAGE_ACCESS_KEY);
        setUser(res);
        // 펍 목록 페이지 이동
        navigate("/login/pub-list");
        return;
      }
      // 일반 유저 로그인
      if (res) {
        setUser(res);
        // 유저가 가진 펍이 1개인 경우 펍 선택없이 로그인
        if (pubOwners.length === 1) {
          try {
            await authPubOwnerToken(pubOwners[0].pubId);
            localStorage.setItem(
              LOCAL_STORAGE_PUB_ID,
              String(pubOwners[0].pubId),
            );
            navigate("/");
          } catch (e) {
            alert("관리자 로그인 정보를 확인해주세요.");
          }
        }
        // 유저가 가진 펍이 여러개일 경우 펍 선택 컴포먼트 노출
        if (pubOwners.length > 0) {
          setPubOwners(pubOwners);
        }
      }
    },
    [navigate, setUser],
  );

  // 일반 로그인 -> 해당 함수 바로 호출
  // sns 로그인 -> 일반 로그인 후 토큰으로 셀러 로그인
  const handleSellerLogin = useCallback(
    async (accessToken?: string) => {
      try {
        localStorage.removeItem(LOCAL_STORAGE_PUB_ID);
        const result = await sellerLogin({
          accessToken,
          textId: accessToken ? undefined : textId,
          password: accessToken ? undefined : password,
        });
        const tokenValue = result.payload.accessToken
          ? `${LOCAL_STORAGE_TOKEN_TYPE} ${result.payload.accessToken}`
          : null;
        if (tokenValue) {
          // 토큰 및 유저 아이디 저장
          localStorage.setItem(LOCAL_STORAGE_ACCESS_KEY, tokenValue);
          localStorage.setItem(
            LOCAL_STORAGE_USER_ID,
            String(result.payload.userId),
          );
        }
        // 본사펍 필터링
        const filteredPubOwners = result.payload.pubOwners.filter(
          (item) => item.pubType === "FRANCHISE",
        );
        // 유저 정보 조회 및 펍 선택
        await handleAuthDetail(tokenValue ?? undefined, filteredPubOwners);
      } catch (e: any) {
        alert(e.message || "로그인 정보를 확인해주세요.");
      }
    },
    [handleAuthDetail, textId, password],
  );

  // sns 로그인 처리
  // sns 로그인으로 토큰 발급 후 셀러 로그인
  const handleLogin = useCallback(
    async (nextSnsData?: LoginRouterState) => {
      const loginTextId = nextSnsData?.textId || textId;
      const params = {
        textId: loginTextId,
        password,
        loginType: nextSnsData?.loginType,
        accessToken: nextSnsData?.accessToken,
      };
      try {
        // 일반 로그인 진행
        const result = await authLogin(params);
        if (!result?.accessToken) {
          alert("로그인에 실패했습니다.");
          return;
        }
        // 셀러 로그인 진행
        await handleSellerLogin(result.accessToken);
      } catch (err: any) {
        if (err.code === 4007) {
          if (
            params.loginType !== "SIMPLE_NAVER" &&
            params.loginType !== "SIMPLE_APPLE"
          ) {
            alert("탈퇴된 계정입니다.");
            return;
          }
          alert("등록된 계정이 없습니다. 러너러너 앱에서 먼저 가입해주세요.");
          return;
        }
        alert(err.message || "로그인에 실패했습니다.");
      }
    },
    [textId, password, handleSellerLogin],
  );

  const sns = useSnsLogin({ handleLogin });

  useEffect(() => {
    snsStateRef.current = {
      setIsNaver: sns.setIsNaver,
      setNaverEmail: sns.setNaverEmail,
      setNaverPopupType: sns.setNaverPopupType,
    };
  }, [sns.setIsNaver, sns.setNaverEmail, sns.setNaverPopupType]);

  // sns 로그인 콜백 처리 (네이버 로그인 콜백)
  useEffect(() => {
    // 이미 처리된 경우 종료
    if (snsCallbackHandled.current) return;

    // 콜백 파라미터 조회
    const urlData = readSnsCallbackParams();
    const locationState = location.state as LoginRouterState | null;

    // 앱에서 전송한 sns 데이터 조회
    const nextSnsData =
      urlData?.loginType && urlData?.accessToken
        ? urlData
        : locationState?.loginType && locationState?.accessToken
          ? {
              redirect: locationState.redirect || "",
              loginType: locationState.loginType,
              accessToken: locationState.accessToken,
              textId: locationState.textId,
            }
          : null;
    if (!nextSnsData?.loginType || !nextSnsData?.accessToken) return;

    // 완료 처리
    snsCallbackHandled.current = true;

    // 정보 저장 후 url 초기화
    window.history.replaceState(null, "", window.location.pathname);

    // 로그인 페이지로 이동
    if (location.state?.loginType) {
      navigate("/login", { replace: true });
    }

    // 콜백 정보를 바탕으로 로그인 진행
    void handleLogin(nextSnsData);
  }, [handleLogin, location.state, navigate]);

  const handleNaverLogout = useCallback(async () => {
    clearCredential();
    setUser(null);
    localStorage.removeItem(LOCAL_STORAGE_ACCESS_KEY);
    const token = localStorage.getItem("naver_token");
    try {
      await deleteNaverToken(token!);
      if (sns.naverPopupType === "login") {
        alert("네이버 계정 연결 해제에 성공했습니다.");
      } else {
        alert("네이버 계정 연결 해제에 실패했습니다.");
      }
      localStorage.removeItem("naver_token");
      sns.setIsNaver(false);
    } catch {
      alert("네이버 계정 연결 해제에 실패했습니다.");
    }
  }, [setUser, sns]);

  return {
    textId,
    setTextId,
    password,
    setPassword,
    pubOwners,
    handleNaverLogout,
    handleSellerLogin,
    ...sns,
  };
};

export default useLogin;
